<template>
  <div class="NetworkProject">
    <h1>Specialization - Networking Library</h1>
        <h2>The goal</h2>
        <p>
          This project started as a networking project about lag compensation and input prediction, 
          but ended up being more about library and interface design while learning about networking as a concept.
        </p>
        <p>
          The end product is a library that does most of the setup for a UDP Client and server for games, 
          with an easy interface for serialization and bitpacking.
        </p>
        <h2>The project</h2>
        <p>
          The project is split into 3 parts, the Client, the server and the shared Library. This is to minimize copied code, 
          as a lot of the functionality is very similar between the server and the client,
          Especially since it is using UDP.
        </p>
        <p>
          There are a lot of classes that try to simplify the networking process. For example, the address class
          has a lot of different constructors to make it easy to use both ipv4 and ipv6, inputting both via strings and numbers.
        </p>
<pre><code class="hljs cpp">
class Address //Incomplete, just the constructors
{
public:
  Address();
  explicit Address(uint8_t aA, uint8_t aB, uint8_t aC, uint8_t aD, uint16_t aPort = 0);
  explicit Address(uint32_t aAddress, uint16_t aPort = 0);
  explicit Address(uint16_t aA, uint16_t aB, uint16_t aC, uint16_t aD,
    uint16_t aE, uint16_t aF, uint16_t aG, uint16_t aH, uint16_t aPort = 0);
  explicit Address(uint16_t aAddress[], uint16_t aPort = 0);
  explicit Address(const char* aAddress);
  explicit Address(const char* aAddress, uint16_t aPort);
  explicit Address(const sockaddr_storage& aAddress);
  //Continues...
};
</code></pre>

<p>
  This is the bitpacking function, which abstracts to the point that the end user doesn't have to think about it. 
  I learned a lot about low level code and bitwise operators, which will help me write more effective code in the future.
</p>
<pre><code class="hljs cpp">void Spooder::BitWriter::WriteBits(void* aData, int aBitsToWrite)
{
  assert(aBitsToWrite > 0);
  myScratch |= (*(uint64_t*)aData) &lt;&lt; myScratchBits; 
  myScratchBits += aBitsToWrite; 
  if (myScratchBits >= 32) 
  {
    myBuffer[myWordIndex] = myScratch; 
    myWordIndex++; 
    myScratch >>= 32; 
    myScratchBits -= 32; 
  }
  myBitsWritten += aBitsToWrite;
}
</code></pre>

        <p>
          A lot of this code is taken and interpreted from <a href="https://gafferongames.com/categories/building-a-game-network-protocol/">Gaffer on games excellent articles</a>, the main
          resource for this project
        </p>
        <h2>Using the library</h2>
        <p>
          One of the main goals with this project was providing a simple interface for the end user, so 
          that even if you don't know that much about networking, you can get something up and running.
        </p>
        <p>
          The interface looks as follows:
        </p>
<pre><code class="hljs cpp">struct TestPacket : public Packet
{
  int myA;
  float myB;

  TestPacket()
    : Packet(PACKET_TEST_PACKET){}

  template &lt;typename Stream&gt;
  bool Serialize(Stream&amp; aStream)
  {
    SERIALIZE_INT(aStream, myA[i], 0, 255);
    SERIALIZE_FLOAT_COMPRESSED(aStream, myB, 0.f, 256.f, 0.01f);

    return true;
  }
  SPOODER_DECLARE_VIRTUAL_SERIALIZE_FUNCTIONS();
};

</code></pre>
        <p>
          The serialize macros return false if the serialization fails, so the failchecking
          is hidden, but is still there, making the interface simpler to use, at the cost of
          being a bit harder to understand in the background.
        </p>
        <p>
          In the serialize macros there is also some extra parameters, for minimum and maximum values.
          This allows the user to specify a range of the variable, which translates into automatic bitpacking, 
          to save space in the packets sent to and from the server.
          The float has two different serialize functions, one for normal floats and one for compressed values as shown here.
          This is also to save space in the packets for cases where you don't need the full 32 bits of accuracy.
        </p>
        <p>
          Lastly, the <code>SPOODER_DECLARE_VIRTUAL_SERIALIZE_FUNCTIONS</code> macro automatically declaring
          serializing functions for read and writestreams, so that we can use a virtual packet class, but as the definition of
          the function is the same across all packets, we just use a macro.
        </p>

        <h2>The future</h2>
        <p>
          I wanted to add way more things to the library, but as time was limited I had to limit my scope.
          First I would add support for reliable messages, an essential feature in a networking library.
          I would also add support for packet fragmentation, as I think it would be very useful and interesting to implement.
          Next, my plan was to add support for different update rates for each networked struct, to allow for more
          effective communication over the network.
        </p>
        <br>
        <br>
        <br>
        <br>
        <br>
        <br>
        <br>
  </div>
</template>

<script>
import hljs from 'highlight.js'

export default {
  components: {
  },
  data(){
    return{
      code: "printf(\"nya\");"
    }
  },
  mounted(){
    hljs.highlightAll();
    document.title = "Benjamin Mättö - Networking library"
  }
}
</script>



<style scoped>
@media screen and (min-width: 601px) {
  .NetworkProject{
    margin-left: 20%;
    margin-right: 20%;
  }
  h2{
    font-size: 72px;
  }
}
@media screen and (max-width: 600px) {
  .NetworkProject{
    margin-left: 10%;
    margin-right: 10%;
  }
  h2{
    font-size: 45px;
  }
}
  img{
    max-width: 100%;
    height: auto;
  }
  .picture{
    width: 100%;
    height: 100%;
  }
  h2{
    position: relative;
    top: 30px;
    padding: 0 0;
    margin: 0 0;
    text-align: left;
    /*z-index: 2;*/
  }
  pre{
    /*margin-left:10%;*/
    margin-right:10%;
  }
  p code
  {
    font-size: 16px;
  }
  code{
    font-family: 'Hack', monospace;
    white-space: pre;
  }
</style>